import { SearchCard, SearchProgressEvent } from "./types";

// Module-scoped, dependency-free store for the in-chat search "thinking" stream.
// Each Pusher event appends one item; the component renders whatever has streamed
// so far ("one message at a time, previous stays until the next"). No caps.
//
// Per-tool-call `step` lifecycle (BE PR #632): a multi-city search emits several
// `step:active` items that open CONCURRENT spinners (keyed by id) and resolve
// independently on `step:done`. `message`/`card` items may carry a `stepId` to
// render grouped under their step. Steps live ONLY in this in-memory store (never
// session/localStorage — a reload resets the module, so dead spinners can't
// resurrect).
//
// useSyncExternalStore identity rule: getSnapshot() must return a referentially
// stable value between mutations — so we replace the whole snapshot object on
// each change and return the same reference otherwise.
export type SearchProgressStatus = "idle" | "active" | "done";

// One concurrent unit of work (a tool call). `seq` is a monotonic arrival index
// shared with messages so the frozen chip log can interleave step labels and
// un-attributed messages in true order.
export interface SearchStep {
  id: string;
  text?: string;
  status: "active" | "done";
  seq: number;
}

// A status line. `stepId` groups it under a step (when that step exists); absent
// or unmatched → un-attributed (e.g. the opener "On it…").
export interface SearchMessage {
  text: string;
  stepId?: string;
  seq: number;
}

export interface SearchProgressSnapshot {
  status: SearchProgressStatus;
  steps: SearchStep[];
  messages: SearchMessage[];
  cards: SearchCard[];
  header?: string;
  startedAt?: number;
  endedAt?: number;
}

const IDLE: SearchProgressSnapshot = {
  status: "idle",
  steps: [],
  messages: [],
  cards: [],
};

const listeners = new Set<() => void>();
let snapshot: SearchProgressSnapshot = IDLE;
// Monotonic arrival counter; reset on each new search ("start"/"reset").
let seq = 0;
// Page-load gate: a stream is honoured only once we've seen its `start` in THIS
// page session. The Pusher channel is session-scoped (private-search-${sessionId})
// and the sessionId survives a refresh, so a search that began BEFORE a reload
// keeps replaying its mid-stream events after we re-subscribe — without this gate
// those would resurrect a stale search on the fresh page. Cleared on reset(), so
// every new search must re-open with `start`.
let sawStart = false;

const notify = (): void => {
  listeners.forEach((l) => l());
};

const now = (): number => Date.now();

// Per-spinner max-age safety net: the BE can't guarantee a `done` against a
// dropped packet or a hard failure, so each active step auto-resolves after this
// long with no `done`. Generous (a single tool call shouldn't exceed it) — this
// is a backstop, not a pacing timer. Cleared on done/end/reset/start. Tunable.
const STEP_MAX_AGE_MS = 60_000;
const stepTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearStepTimer = (id: string): void => {
  const t = stepTimers.get(id);
  if (t !== undefined) {
    clearTimeout(t);
    stepTimers.delete(id);
  }
};

const clearAllStepTimers = (): void => {
  stepTimers.forEach((t) => clearTimeout(t));
  stepTimers.clear();
};

const armStepTimer = (id: string): void => {
  clearStepTimer(id);
  stepTimers.set(
    id,
    setTimeout(() => {
      stepTimers.delete(id);
      const idx = snapshot.steps.findIndex((s) => s.id === id);
      if (idx < 0 || snapshot.steps[idx].status !== "active") return;
      const steps = snapshot.steps.slice();
      steps[idx] = { ...steps[idx], status: "done" };
      snapshot = { ...snapshot, steps };
      notify();
    }, STEP_MAX_AGE_MS)
  );
};

// Stream-idle backstop for a dropped `end` (the BE can't guarantee it against a
// dropped packet / hard failure). After a long SILENCE — no event of any kind —
// while still active, collapse the bubble as if `end` arrived (resolve spinners +
// mark done). Re-armed on every event, so it fires ONLY on a genuinely dead
// stream, never mid-search (real inter-event gaps are seconds, not a minute).
// This replaces the old isLoading-based collapse, which fired the instant the
// INDEPENDENT LLM reply finished and tore the store down mid-stream.
const STREAM_MAX_IDLE_MS = 60_000;
let streamIdleTimer: ReturnType<typeof setTimeout> | undefined;

const clearStreamIdle = (): void => {
  if (streamIdleTimer !== undefined) {
    clearTimeout(streamIdleTimer);
    streamIdleTimer = undefined;
  }
};

const armStreamIdle = (): void => {
  clearStreamIdle();
  streamIdleTimer = setTimeout(() => {
    streamIdleTimer = undefined;
    if (snapshot.status !== "active") return;
    clearAllStepTimers();
    snapshot = {
      ...snapshot,
      status: "done",
      endedAt: now(),
      steps: snapshot.steps.map((s) => (s.status === "active" ? { ...s, status: "done" } : s)),
    };
    notify();
  }, STREAM_MAX_IDLE_MS);
};

export const SearchProgressStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot(): SearchProgressSnapshot {
    return snapshot;
  },

  apply(evt: SearchProgressEvent): void {
    // Honour a stream only after its `start` this page-load (see `sawStart`). A
    // pre-refresh search replays mid-stream events with no fresh `start` → ignore.
    if (evt?.type === "start") {
      sawStart = true;
    } else if (!sawStart) {
      return;
    }
    switch (evt?.type) {
      case "start":
        seq = 0;
        clearAllStepTimers();
        snapshot = {
          status: "active",
          steps: [],
          messages: [],
          cards: [],
          header: evt.header,
          startedAt: now(),
        };
        break;
      case "step": {
        if (!evt.id) return;
        const idx = snapshot.steps.findIndex((s) => s.id === evt.id);
        if (evt.status === "active") {
          // implicit start: tolerate a step arriving before an explicit "start"
          let nextSteps: SearchStep[];
          if (idx >= 0) {
            // re-activate / relabel an existing step
            nextSteps = snapshot.steps.slice();
            nextSteps[idx] = {
              ...nextSteps[idx],
              status: "active",
              text: evt.text ?? nextSteps[idx].text,
            };
          } else {
            nextSteps = [
              ...snapshot.steps,
              { id: evt.id, text: evt.text, status: "active", seq: seq++ },
            ];
          }
          snapshot = {
            ...snapshot,
            status: "active",
            startedAt: snapshot.startedAt ?? now(),
            steps: nextSteps,
          };
          armStepTimer(evt.id);
          break;
        }
        // status === "done": pair active→done by id. An orphan done (id never
        // seen go active) is a no-op — don't materialise a phantom step.
        if (idx < 0) return;
        clearStepTimer(evt.id);
        const doneSteps = snapshot.steps.slice();
        doneSteps[idx] = { ...doneSteps[idx], status: "done" };
        snapshot = { ...snapshot, steps: doneSteps };
        break;
      }
      case "message":
        if (!evt.text) return;
        // implicit start: tolerate an item arriving before an explicit "start"
        snapshot = {
          ...snapshot,
          status: "active",
          startedAt: snapshot.startedAt ?? now(),
          messages: [...snapshot.messages, { text: evt.text, stepId: evt.stepId, seq: seq++ }],
        };
        break;
      case "card":
        if (!evt.card) return;
        snapshot = {
          ...snapshot,
          status: "active",
          startedAt: snapshot.startedAt ?? now(),
          cards: [...snapshot.cards, { ...evt.card, stepId: evt.stepId }],
        };
        break;
      case "end":
        // Resolve every still-active spinner, then collapse. `end` is the ONLY
        // signal that ends the bubble's working lifetime (BE RULE 1) — a per-step
        // `done` resolves just that row, never the whole bubble.
        clearAllStepTimers();
        snapshot = {
          ...snapshot,
          status: "done",
          endedAt: now(),
          steps: snapshot.steps.map((s) => (s.status === "active" ? { ...s, status: "done" } : s)),
        };
        break;
      default:
        return;
    }
    // Keep the dropped-`end` backstop armed while the stream is live; clear it the
    // moment the stream finishes (done) so a fresh search starts clean.
    if (snapshot.status === "active") armStreamIdle();
    else clearStreamIdle();
    notify();
  },

  reset(): void {
    clearAllStepTimers();
    clearStreamIdle();
    // Re-block until the next search's `start` (so a stale mid-stream replay after
    // a turn reset / page-load can't slip in before the new search opens).
    sawStart = false;
    if (snapshot === IDLE) return;
    snapshot = IDLE;
    notify();
  },
};
